import React, { useEffect, useState } from 'react';
import {
    BooleanBadge,
    CenterCircularProgress,
    LazyBreadcrumb,
    Toolbar,
    useModal,
} from '@europrocurement/flexy-components';
// import {useTheme} from '@mui/system';
import { Container } from '@mui/system';
import {
    AntennesJsonldAntenneRead,
    GroupesJsonldGroupReadCollection,
    MarquesJsonldSocieteMarqueReadCollection,
    PolesJsonldDefPoleReadCollection,
    ServicesJsonldServiceReadCollection,
    UtilisateursApiApiUtilisateursIdDeleteRequest,
    UtilisateursApiApiUtilisateursIdsyncPatchRequest,
    UtilisateursJsonldUtilisateurPatchUtilisateur,
    UtilisateursJsonldUtilisateurRead,
    UtilisateursUtilisateurUpdateUtilisateurUtilisateurPatchUtilisateur,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { useParams } from 'react-router';
import {
    groupesSelector,
    marquesSelector,
    polesSelector,
    servicesSelector,
    antennesSelector,
    useSociete,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
    utilisateursSelector,
} from '@europrocurement/l2d-domain';
import { useDispatch } from 'react-redux';
import { AxiosResponse } from 'axios';
import { B2DLogoIcon } from '@europrocurement/l2d-icons';
// import { faLockAlt, faLockOpenAlt } from '@fortawesome/pro-solid-svg-icons';
import {
    errorNotification,
    successNotification,
} from '@europrocurement/flexy-components/src/components/atoms/NotificationToasters/notificationToasters';
import { ValidationModal } from '@europrocurement/l2d-modules/modules/ValidationModal/ValidationModal';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import {
    utilisateursBasePath,
    utilisateursListePath,
    utilisateursUtilisateursPath,
} from '../../constants/paths';
import { User } from '../../types/type';
import { ErrorPageGeneric } from '../../ErrorPageGeneric';
import { getValidationMessage } from '../../functions/getValidationMessages';
import { UserTabs } from '../../components/Utilisateur/UserTabs';
import { companyOptions, employerCompanyOptions } from '../../functions/createUserFormStructure';
import { getSelectsOptions } from '../../functions/getSelectsOptions';
import { UserFormReturn } from '../../components/Utilisateur/UserForm/UserForm';

type UserData = UtilisateursJsonldUtilisateurRead | UtilisateursJsonldUtilisateurPatchUtilisateur;

type PatchPayload =
    | UserFormReturn
    | UtilisateursUtilisateurUpdateUtilisateurUtilisateurPatchUtilisateur;

const UtilisateurFiche: React.FunctionComponent = function () {
    const { idUtilisateur } = useParams();
    const xIdSociete = useSociete();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { selectUtilisateurs, utilisateurApi } = useUtilisateurService();
    const keycloak = UseKeycloakService();
    const { modalActions } = useModal();

    const [userData, setUserData] = useState<UserData>();
    const dataSource = useUtilisateursServiceSelector(utilisateursSelector);
    const services = useUtilisateursServiceSelector(servicesSelector).data;
    const poles = useUtilisateursServiceSelector(polesSelector).data;
    const marques = useUtilisateursServiceSelector(marquesSelector).data;
    const groupes = useUtilisateursServiceSelector(groupesSelector).data;
    const antennes = useUtilisateursServiceSelector(antennesSelector).data;
    const user = dataSource.selected;

    useEffect(() => {
        if (idUtilisateur) {
            // unselect the selected user to remove the previous user's data
            dispatch({ type: 'utilisateur/deletemainSelected' });

            // select the current user (same as the route)
            dispatch(selectUtilisateurs({ id: idUtilisateur }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // On component mount only

    // Is component ready to render content?
    useEffect(() => {
        const userLoaded = idUtilisateur && user && `${user.id}` === idUtilisateur;
        const dataSourceLoaded = dataSource.selectedStatus === 'succeeded';

        // if the user is the right one and his data have been retrieved, put them to the state
        if (userLoaded && dataSourceLoaded) {
            setUserData(user);
        }
    }, [dataSource.selectedStatus, idUtilisateur, user]);

    if (!idUtilisateur) {
        return null;
    }

    if (dataSource.selectedStatus === 'failed') {
        return (
            <ErrorPageGeneric
                message="Utilisateur introuvable"
                statusCode={404}
                title="Page introuvable"
                description="This is Not Found page"
                details="L'identifiant saisit ne correspond à aucun utilisateur. Merci de vérifier votre saisie."
                returnButtonLabel="Retour à la liste des utilisateurs"
                returnUrl="/utilisateurs/utilisateurs"
                logo={<B2DLogoIcon style={{ height: '64px', marginTop: '40px' }} />}
            />
        );
    }

    if (!userData) {
        return <CenterCircularProgress sx={{ height: '70vh' }} />;
    }

    const loggedUser: string | undefined = keycloak.getTokenParsed().preferred_username;

    const { servicesOptions, polesOptions, marquesOptions, groupesOptions, antennesOptions } =
        getSelectsOptions({
            services,
            poles,
            marques,
            groupes,
            antennes,
        });

    /**
     * Function that get the IRI as parameter, extract the id and get the object to return
     * @param stringToFormat IRI string
     */
    const getObjectFromIRI = (
        stringToFormat: string | null | undefined,
        objectKey: keyof User,
    ):
        | ServicesJsonldServiceReadCollection
        | MarquesJsonldSocieteMarqueReadCollection
        | PolesJsonldDefPoleReadCollection
        | GroupesJsonldGroupReadCollection
        | AntennesJsonldAntenneRead
        | null
        | undefined => {
        if (stringToFormat !== null && stringToFormat !== undefined) {
            const id = parseInt(stringToFormat.replace(/[^0-9]/g, ''), 10);

            switch (objectKey) {
                case 'service':
                    return services.find((match) => match.id === id);

                case 'pole':
                    return poles.find((match) => match.id === id);

                case 'marque':
                    return marques.find((match) => match.idMarque === id);

                case 'idGroupeGie':
                    return groupes.find((match) => match.id === id);

                case 'antenne':
                    return antennes.find((match) => match.idAntenne === id);

                default:
                    break;
            }
        } else if (stringToFormat === undefined) {
            return undefined;
        }

        return null;
    };

    /**
     * On Patch API success, set the userData with the new value to rerender the current user data.
     * Notifies that the modification is done
     * @param response API Patch response success return
     */
    const onPatchSuccess = (
        response: AxiosResponse<UtilisateursJsonldUtilisateurPatchUtilisateur>,
    ) => {
        const { data } = response;

        // extract the id of the data from the IRI with regex
        const newService = getObjectFromIRI(data.service, 'service') as
            | ServicesJsonldServiceReadCollection
            | null
            | undefined;

        const newMarque = getObjectFromIRI(data.marque, 'marque') as
            | MarquesJsonldSocieteMarqueReadCollection
            | null
            | undefined;

        const newPole = getObjectFromIRI(data.pole, 'pole') as
            | PolesJsonldDefPoleReadCollection
            | null
            | undefined;

        const newGroupe = getObjectFromIRI(data.idGroupeGie, 'idGroupeGie') as
            | GroupesJsonldGroupReadCollection
            | null
            | undefined;

        const newAntenne = getObjectFromIRI(data.antenne, 'antenne') as
            | AntennesJsonldAntenneRead
            | null
            | undefined;

        // Those data are not returned by the patch, need to retrieve them and add it in the state for the information user update
        const newLibelleSociete = companyOptions.find(
            (company) => company.value === data.idSociete,
        )?.label;

        const newLibelleSocieteEmployeur = employerCompanyOptions.find(
            (company) => company.value === data.idSocieteEmployeur,
        )?.label;

        // replace the patch return object by the formated version to update the state
        const newData: UtilisateursJsonldUtilisateurRead = {
            ...data,
            service: newService,
            marque: newMarque,
            pole: newPole,
            antenne: newAntenne,
            idGroupeGie: newGroupe,
            libelleSociete: newLibelleSociete,
            libelleSocieteEmployeur: newLibelleSocieteEmployeur,
        };

        setUserData(newData);

        successNotification("L'utilisateur a bien été modifié");

        modalActions.close();
    };

    /**
     * Function that get the payload, format it if necessary, then patch it into the API
     * @param payload object from the patch or the UserInfos modification form
     * @param dirtyFields object that caught only modified fiels from the modification form
     */
    const patch = (
        payload: PatchPayload,
        dirtyFields?: {
            [key: string]: boolean;
        },
    ) => {
        let formValues = payload;

        if (typeof dirtyFields !== 'undefined') {
            const dirtyFieldKeys = Object.keys(dirtyFields) as Array<keyof PatchPayload>;

            // add only the modified form values to the payload object for the patch
            formValues = dirtyFieldKeys.reduce(
                (acc, current) => ({ ...acc, [current]: payload[current] }),
                {},
            );
        }

        const requestObject = {
            id: idUtilisateur,
            xIdSociete,
            utilisateursUtilisateurUpdateUtilisateurUtilisateurPatchUtilisateur: formValues,
        };

        utilisateurApi
            .apiUtilisateursIdPatch(requestObject)
            .then((response) => {
                onPatchSuccess(response);
            })
            .catch((error) => {
                console.error(error);
                if (error.response.data.status === 422) {
                    errorNotification('Cet email existe déjà');
                } else {
                    errorNotification();
                }
            });
    };

    /**
     * Send request to the API to delete the selected user.
     * Update the userData state to rerender component ans close modal.
     */
    const deleteUser = () => {
        console.log(idUtilisateur);

        utilisateurApi
            .apiUtilisateursIdDelete({
                id: idUtilisateur,
            } as UtilisateursApiApiUtilisateursIdDeleteRequest)
            .then(() => {
                const updateUser = {
                    ...userData,
                    isDeleted: true,
                };
                setUserData(updateUser);

                successNotification("L'utilisateur a bien été supprimé.");
            })
            .catch((error) => {
                console.error(error);
                errorNotification(
                    "Une erreur est survenue lors de la suppression de l'utilisateur.",
                );
            })
            .finally(() => modalActions.close());
    };

    const breadcrumbPath = [
        {
            path: 'utilisateurs',
            link: `${utilisateursUtilisateursPath}`,
        },
        `utilisateur  ${userData.prenom} ${userData.nom}`,
        '',
    ];

    /**
     * Calls the patch function to update user (or deleteUser only in the case of user deletion).
     * @param payload the data to update
     */
    const handleModalSubmit = (
        payload: UtilisateursUtilisateurUpdateUtilisateurUtilisateurPatchUtilisateur,
    ) => {
        if (payload.isDeleted) {
            deleteUser();
        } else {
            patch(payload);
        }
    };

    /**
     * Get the values of the switch toggle to open a validation modal.
     * @param payload patch payload object
     * @param field the toggle key
     * @param value the switch value
     */
    const handleValidationModal = (
        payload: UtilisateursUtilisateurUpdateUtilisateurUtilisateurPatchUtilisateur,
        field: string,
        value: boolean,
    ) => {
        const message = getValidationMessage(value, field);

        modalActions.call(
            <ValidationModal
                message={message}
                onSubmit={() => handleModalSubmit(payload)}
            />,
        );
    };

    const synchroniseWithKCAccount = (userId: number) => {
        const requestParam: UtilisateursApiApiUtilisateursIdsyncPatchRequest = {
            id: userId.toString(),
            utilisateursUtilisateurSyncUtilisateur: {},
        };

        utilisateurApi
            .apiUtilisateursIdsyncPatch(requestParam)
            .then((response) => {
                successNotification('La synchronisation a bien été effectuée.');
                setUserData(response.data);
            })
            .catch((error) => {
                console.error(error);
                errorNotification(
                    'Une erreur est survenue lors de la synchronisation avec Keycloak.',
                );
            });
    };

    return (
        <Container maxWidth="lg">
            <Toolbar previousPath={`/${utilisateursBasePath}/${utilisateursListePath}`}>
                <LazyBreadcrumb path={breadcrumbPath} />
                <BooleanBadge
                    label={userData.isDeleted ? 'Compte désactivé' : 'Compte actif'}
                    value={!userData.isDeleted}
                    icon
                    toggleAction={() =>
                        handleValidationModal(
                            { isDeleted: !userData.isDeleted },
                            'isDeleted',
                            userData.isDeleted as boolean,
                        )
                    }
                />
            </Toolbar>

            <UserTabs
                services={servicesOptions}
                poles={polesOptions}
                groupes={groupesOptions}
                handlePatchData={patch}
                handlePatchInfos={patch}
                user={userData as User}
                loggedUser={loggedUser}
                marques={marquesOptions}
                antennes={antennesOptions}
                handleValidationModal={handleValidationModal}
                synchroniseWithKCAccount={synchroniseWithKCAccount}
            />
        </Container>
    );
};

export default UtilisateurFiche;
